<template>
  <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
    <!-- Header -->
    <div class="mb-6 sm:mb-8">
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
        {{ t('app.pending_payments.title') }}
      </h1>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {{ t('app.pending_payments.subtitle') }}
      </p>
    </div>

    <!-- Loading State -->
    <LoadingSpinner
      v-if="loading"
      size="xl"
      color="primary"
      :show-text="false"
    />

    <!-- Empty State -->
    <EmptyState
      v-else-if="!payments || payments.length === 0"
      icon="folder"
      :title="t('app.pending_payments.no_payments')"
      :description="t('app.pending_payments.all_processed')"
      variant="minimal"
      size="md"
    />

    <!-- Payments List -->
    <div
      v-else
      class="space-y-4"
    >
      <PaymentCard
        v-for="payment in payments"
        :key="payment.id"
        :payment="payment"
        @approve="handleApprove"
        @reject="handleReject"
      />
    </div>

    <!-- Approve Modal -->
    <ApprovePaymentModal
      :show="showApproveModal"
      :payment="selectedPayment"
      :processing="processing"
      @close="closeModals"
      @confirm="confirmApprove"
    />

    <!-- Reject Modal -->
    <RejectPaymentModal
      v-model="rejectionReason"
      :show="showRejectModal"
      :processing="processing"
      @close="closeModals"
      @confirm="confirmReject"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '@/composables/useToast';
import { usePendingPayments, type Payment } from '@/composables/usePendingPayments';
import PaymentCard from '@/components/payments/PaymentCard.vue';
import ApprovePaymentModal from '@/components/payments/ApprovePaymentModal.vue';
import RejectPaymentModal from '@/components/payments/RejectPaymentModal.vue';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';
import EmptyState from '@/components/base/EmptyState.vue';

const { t } = useI18n();
const { showSuccess, showError } = useToast();

// Composable
const {
  payments,
  loading,
  error,
  processing,
  loadPayments,
  approvePayment,
  rejectPayment
} = usePendingPayments();

// Modal state
const showApproveModal = ref(false);
const showRejectModal = ref(false);
const selectedPayment = ref<Payment | null>(null);
const rejectionReason = ref('');

// Handlers
const handleApprove = (payment: Payment) => {
  selectedPayment.value = payment;
  showApproveModal.value = true;
};

const handleReject = (payment: Payment) => {
  selectedPayment.value = payment;
  rejectionReason.value = '';
  showRejectModal.value = true;
};

const closeModals = () => {
  showApproveModal.value = false;
  showRejectModal.value = false;
  selectedPayment.value = null;
  rejectionReason.value = '';
};

const confirmApprove = async () => {
  if (!selectedPayment.value) return;

  const success = await approvePayment(selectedPayment.value.id);
  
  if (success) {
    showSuccess(t('app.pending_payments.approved_success'));
    closeModals();
  } else if (error.value) {
    showError(error.value);
  }
};

const confirmReject = async () => {
  if (!selectedPayment.value || rejectionReason.value.trim().length < 10) {
    showError(t('app.pending_payments.reason_min_length'));
    return;
  }

  const success = await rejectPayment(selectedPayment.value.id, rejectionReason.value);
  
  if (success) {
    showSuccess(t('app.pending_payments.rejected_success'));
    closeModals();
  } else if (error.value) {
    showError(error.value);
  }
};

// Initialize
onMounted(() => {
  loadPayments();
});
</script>
